<script setup lang="ts">
/**
 * The landing pane: how many accounts, what is waiting for a decision, and
 * the economy's own live shape.
 *
 * The KYC tile carries a `flag` class when non-zero, because a pending KYC
 * is the one number in Accounts that is *work* rather than information — the
 * rest are things to know, that one is a thing to do.
 *
 * Economy numbers come from a second, separate request (`economyStats`) —
 * `GET /api/admin/economy-stats` is a game-owned endpoint distinct from
 * core's `GET /api/admin/stats`, so it is fetched and rendered independently
 * rather than folded into the Accounts section above it.
 */
import { computed } from 'vue';

import CurrencyIcon from '@/ui/CurrencyIcon.vue';

import { adminApi } from './admin-api';
import PaymentConfigToggles from './PaymentConfigToggles.vue';
import { usePane } from './pane';

const stats = usePane(() => adminApi.stats());
const economy = usePane(() => adminApi.economyStats());
const status = usePane(() => adminApi.serverStatus());

/** `server-status` is a bag of whatever the backend felt like reporting. */
const statusRows = computed(() =>
  Object.entries(status.data.value ?? {}).map(([key, value]) => ({
    key,
    value: typeof value === 'object' ? JSON.stringify(value) : String(value),
  })),
);
</script>

<template>
  <h1>Overview</h1>
  <p class="lede">Accounts, and anything waiting on an operator.</p>

  <p v-if="stats.error.value" class="bad">{{ stats.error.value }}</p>

  <section v-if="stats.data.value">
    <h2>Accounts</h2>
    <div class="tiles">
      <div class="tile">
        <div class="tile-value">{{ stats.data.value.totalUsers }}</div>
        <div class="tile-label">Total</div>
      </div>
      <div class="tile">
        <div class="tile-value">{{ stats.data.value.newToday }}</div>
        <div class="tile-label">New today</div>
      </div>
      <div class="tile">
        <div class="tile-value">{{ stats.data.value.newThisWeek }}</div>
        <div class="tile-label">New this week</div>
      </div>
      <div class="tile" :class="{ flag: stats.data.value.pendingKyc > 0 }">
        <div class="tile-value">{{ stats.data.value.pendingKyc }}</div>
        <div class="tile-label">KYC waiting</div>
      </div>
    </div>
  </section>
  <p v-else-if="stats.loading.value" class="busy">Loading…</p>

  <p v-if="economy.error.value" class="bad">{{ economy.error.value }}</p>

  <section v-if="economy.data.value">
    <h2>Economy</h2>
    <div class="tiles">
      <div class="tile online">
        <div class="tile-value">{{ economy.data.value.onlineNow }}</div>
        <div class="tile-label">Online now</div>
      </div>
      <div class="tile">
        <div class="tile-value">
          {{ economy.data.value.diamondsInEconomy.toLocaleString('en-GB') }}
          <CurrencyIcon kind="diamonds" :labelled="false" />
        </div>
        <div class="tile-label">Diamonds in economy</div>
      </div>
      <div class="tile">
        <div class="tile-value">
          {{ economy.data.value.coinsInEconomy.toLocaleString('en-GB') }}
          <CurrencyIcon kind="credits" :labelled="false" />
        </div>
        <div class="tile-label">Coins in economy</div>
      </div>
    </div>
  </section>
  <p v-else-if="economy.loading.value" class="busy">Loading…</p>

  <section>
    <h2>Server</h2>
    <p v-if="status.error.value" class="bad">{{ status.error.value }}</p>
    <div v-else-if="statusRows.length" class="tiles">
      <div class="tile" v-for="row in statusRows" :key="row.key">
        <div class="tile-value">{{ row.value }}</div>
        <div class="tile-label">{{ row.key }}</div>
      </div>
    </div>
    <p v-else class="busy">Loading…</p>
  </section>

  <PaymentConfigToggles />
</template>

<style scoped>
/*
 * "Online now" reads as a positive, live number rather than a queue —
 * green rather than the shared accent, and its own selector kept local to
 * this component (not `pane.css`) so a one-off tile variant here cannot
 * collide with another pane's own additions to the shared file.
 */
.tile.online .tile-value {
  color: #4ade80;
}
</style>
